import type { QuoteFragmentAllocation } from '../../../domain/quoteSourceFragment';
import {
  assertFragmentAllocation,
  usesQuoteFragmentAllocation,
} from '../../../domain/quoteSourceFragment';
import { isRentalBusinessMode } from './businessModes';
import type { SystemCalculationContext } from './fieldCalculations';
import {
  evaluateCalculation,
  SYSTEM_CALCULATION_REFS,
  systemCalculationByRef,
} from './fieldCalculations';
import {
  quoteLocalExclusiveLineCents,
  withoutQuoteExclusivePricing,
  withQuoteLocalPricing,
} from './quoteLocalPricing';
import { quoteDiscountForMode } from './quoteModeDiscounts';
import {
  quoteDiscountParticipation,
  quoteItemEffectiveDiscount,
} from './quotePackageGroupPolicy';
import type {
  BusinessModeCode,
  PackageProductLink,
  Quote,
  QuoteItem,
  StableBusinessModeCode,
} from './types';

export type PricingBaseSnapshot = NonNullable<QuoteItem['pricingBaseSnapshot']>;
const MAX_SAFE_CENTS = BigInt(Number.MAX_SAFE_INTEGER);

/** Sum already-rounded money values in cents before converting back to yuan. */
function safeMoneyTotal(values: Iterable<number>, label: string): number {
  let totalCents = 0n;
  for (const value of values) {
    // Unknown values stay unknown so one incomplete source does not crash a
    // quote table or get silently replaced with zero.
    if (Number.isNaN(value)) return Number.NaN;
    if (!Number.isFinite(value)) throw new Error(`${label}超出安全分范围`);
    if (value < 0) throw new Error(`${label}超出安全分范围`);
    const cents = Math.round(value * 100);
    if (!Number.isSafeInteger(cents)) throw new Error(`${label}超出安全分范围`);
    totalCents += BigInt(cents);
    if (totalCents > MAX_SAFE_CENTS) throw new Error(`${label}超出安全分范围`);
  }
  return Number(totalCents) / 100;
}

function safeCentsTotal(values: Iterable<number>, label: string): number {
  let totalCents = 0n;
  for (const cents of values) {
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error(`${label}超出安全分范围`);
    totalCents += BigInt(cents);
    if (totalCents > MAX_SAFE_CENTS) throw new Error(`${label}超出安全分范围`);
  }
  return Number(totalCents);
}

type PricedLine = Pick<
  QuoteItem,
  'quantity' | 'discount' | 'pricingSnapshot'
> & {
  deviceFieldSnapshot?: {
    quoteLocalPricing?: NonNullable<
      QuoteItem['deviceFieldSnapshot']
    >['quoteLocalPricing'];
    [key: string]: unknown;
  };
} & Partial<
    Pick<
      QuoteItem,
      | 'formalPlanOccurrence'
      | 'pricingBaseSnapshot'
      | 'packageItemPriceSnapshot'
      | 'packageItemMonthlySubtotalSnapshot'
      | 'packageRelationQuantitySnapshot'
      | 'planDiscountSnapshot'
      | 'planProductPriceSnapshot'
      | 'planProductSubtotalSnapshot'
      | 'planId'
      | 'localPlanInstanceId'
      | 'sourceType'
      | 'sourceFragment'
      | 'fragmentAllocation'
    >
  >;

/** 临时方案标记由有效 group/instance 投影，不能伪造目录 planId。 */
export function hasQuotePlanPricing(
  item: Pick<PricedLine, 'planId' | 'localPlanInstanceId'>,
): boolean {
  return Boolean(item.planId || item.localPlanInstanceId);
}

/** 经来源预检的报价内方案成员复用普通方案单品与小计公式。 */
export function applyQuoteLocalPlanPricing(
  item: QuoteItem,
  context: {
    instanceId: string;
    memberId: string;
    discountPercent: number;
    packageQuantity: number;
  },
): QuoteItem {
  if (!context.instanceId || !context.memberId)
    throw new Error('报价内方案成员身份缺失');
  assertDiscountPercent(context.discountPercent, '方案专属折扣');
  assertQuoteQuantity(context.packageQuantity);
  const packagePrice = item.packageItemPriceSnapshot;
  if (packagePrice === undefined) throw new Error('方案成员缺少冻结套餐单品价');
  assertNonNegativeMoney(packagePrice, '套餐单品价');
  const planDiscount = quoteItemEffectiveDiscount(
    item,
    context.discountPercent,
  );
  const subtotal = planProductSubtotal(
    packagePrice,
    planDiscount,
    item.quantity,
  );
  const exclusiveLineCents = Math.round(subtotal * 100);
  if (!Number.isSafeInteger(exclusiveLineCents) || exclusiveLineCents < 0)
    throw new Error('方案金额超出安全范围');
  return withQuoteLocalPricing(
    {
      ...withoutQuoteExclusivePricing(item),
      localPlanInstanceId: context.instanceId,
      localPlanMemberId: context.memberId,
      planDiscountSnapshot: planDiscount,
      planProductPriceSnapshot: planProductPrice(packagePrice, planDiscount),
      planProductSubtotalSnapshot: subtotal,
    },
    {
      exclusiveDiscountPercent: String(planDiscount),
      exclusiveLineCents,
      exclusiveLineBasisCents: exclusiveLineCents,
      exclusiveLineBasisQuantity: context.packageQuantity,
    },
  );
}

function assertNonNegativeMoney(
  value: number | null,
  label: string,
): asserts value is number {
  if (value === null) throw new Error(`${label}待维护，暂不可计算`);
  if (!Number.isFinite(value) || value < 0)
    throw new Error(`${label}必须是大于等于 0 的有限金额`);
}

function evaluateApprovedSystemCalculation(
  ref: string,
  context: SystemCalculationContext,
  failureMessage: string,
): number {
  const definition = systemCalculationByRef(ref);
  if (!definition) throw new Error(`缺少统一计算定义：${ref}`);
  const result = evaluateCalculation(definition, {}, { system: context });
  if (!result.ok || result.value === undefined)
    throw new Error(result.error ?? failureMessage);
  return result.value;
}

export function assertDiscountPercent(value: number, label = '折扣'): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${label}必须是大于等于 0 的有限数值`);
  }
}

function discountedLineCents(baseCents: number, discount: number): number {
  assertDiscountPercent(discount);
  if (!Number.isSafeInteger(baseCents) || baseCents < 0)
    throw new Error('折扣前金额超出安全分范围');
  const discountBasisPoints = BigInt(Math.round(discount * 100));
  const result = (BigInt(baseCents) * discountBasisPoints + 5_000n) / 10_000n;
  if (result > BigInt(Number.MAX_SAFE_INTEGER))
    throw new Error('折后金额超出安全分范围');
  return Number(result);
}

export function assertQuoteQuantity(value: number): void {
  if (!Number.isInteger(value) || value <= 0)
    throw new Error('数量必须是大于 0 的整数');
}

export function createPricingBaseSnapshot(
  businessModeCode: BusinessModeCode,
  facts: {
    rentalPrice?: number | null;
    retailPriceTax13?: number | null;
    taxInclusivePlanPrice?: number | null;
    packageItemPrice?: number | null;
    legacyUnitPrice?: number | null;
  },
): PricingBaseSnapshot {
  let kind: PricingBaseSnapshot['kind'];
  let amount: number | null | undefined;
  if (businessModeCode === 'buyout') {
    kind = 'buyout-tax13';
    amount = facts.taxInclusivePlanPrice;
  } else if (facts.packageItemPrice !== undefined) {
    kind = 'package-item';
    amount = facts.packageItemPrice;
  } else if (isRentalBusinessMode(businessModeCode)) {
    kind = 'rental';
    amount = facts.rentalPrice;
  } else if (businessModeCode === 'full' || businessModeCode === 'trade') {
    kind = 'retail-tax13';
    amount = facts.retailPriceTax13;
  } else {
    kind = 'legacy-unclassified';
    amount = facts.legacyUnitPrice;
  }
  if (amount == null)
    throw new Error('当前业务模式缺少明确计价基础（价格待维护，暂不可报价）');
  assertNonNegativeMoney(amount, '计价基础');
  return { kind, amount, calculationVersion: 'pricing-v2' };
}

export function repriceQuoteItem(
  item: QuoteItem,
  businessModeCode: BusinessModeCode,
): QuoteItem {
  const next: QuoteItem = {
    ...item,
    pricingBaseSnapshot: createPricingBaseSnapshot(businessModeCode, {
      rentalPrice: item.deviceFieldSnapshot?.rentalPrice,
      retailPriceTax13: item.deviceFieldSnapshot?.retailPriceTax13,
      taxInclusivePlanPrice: item.deviceFieldSnapshot?.taxInclusivePlanPrice,
      packageItemPrice: item.packageItemPriceSnapshot,
      legacyUnitPrice: item.pricingBaseSnapshot?.amount,
    }),
  };
  if (item.formalPlanOccurrence) {
    // 切换仅改变计价视图与派生金额，版本、来源系数及原始价格继续使用本行冻结事实。
    next.formalPlanOccurrence = {
      ...item.formalPlanOccurrence,
      pricingView: occurrencePricingView(businessModeCode),
    };
    next.formalPlanOccurrence.sourceLineCents = item.sourceFragment
      ? (item.fragmentAllocation as QuoteFragmentAllocation)
          .wholeRentalLineCents
      : occurrenceSourceLineCents(next);
  }
  return next;
}

type QuoteModeSwitchContext = Pick<Quote, 'items' | 'businessModeCode'> &
  Partial<
    Pick<Quote, 'containerInstances' | 'quoteGroups' | 'localPlanInstances'>
  >;

function hasActiveQuoteModeContent(quote: QuoteModeSwitchContext): boolean {
  return (
    quote.items.length > 0 ||
    (quote.containerInstances?.length ?? 0) > 0 ||
    (quote.quoteGroups?.length ?? 0) > 0 ||
    (quote.localPlanInstances ?? []).some(
      (instance) => instance.status === 'active',
    )
  );
}

export function quoteBusinessModeSwitchBlockReason(
  quote: QuoteModeSwitchContext,
  mode: StableBusinessModeCode,
): string | null {
  if (!hasActiveQuoteModeContent(quote)) return null;
  const currentIsRental = isRentalBusinessMode(quote.businessModeCode);
  const targetIsRental = isRentalBusinessMode(mode);
  const crossesRentalPurchaseBoundary =
    (currentIsRental && mode === 'buyout') ||
    (quote.businessModeCode === 'buyout' && targetIsRental);
  return crossesRentalPurchaseBoundary
    ? '已有清单，租购类型已锁定；如需另一类型请新建报价'
    : null;
}

/** 跨类切换先记住当前槽并恢复目标槽；租赁别名互切只更新展示标识，保留金额和折扣。 */
export function switchQuoteBusinessMode<T extends Quote>(
  quote: T,
  mode: StableBusinessModeCode,
  label: string,
): T {
  const blockReason = quoteBusinessModeSwitchBlockReason(quote, mode);
  if (blockReason) throw new Error(blockReason);
  if (
    mode !== quote.businessModeCode &&
    isRentalBusinessMode(quote.businessModeCode) &&
    isRentalBusinessMode(mode)
  )
    return {
      ...quote,
      businessModeCode: mode,
      businessModeLabelSnapshot: label,
      businessModeRequiresReselection: false,
    };
  const overall = { ...quote.modeOverallDiscounts };
  if (
    quote.businessModeCode !== 'trade' &&
    quote.businessModeCode !== 'service'
  )
    overall[quote.businessModeCode] = quote.overallDiscount;
  return {
    ...quote,
    businessModeCode: mode,
    businessModeLabelSnapshot: label,
    businessModeRequiresReselection: false,
    overallDiscount:
      mode === quote.businessModeCode
        ? quote.overallDiscount
        : (overall[mode] ?? 100),
    modeOverallDiscounts: overall,
    items: quote.items.map((item) => {
      const modes = { ...item.modeDiscounts };
      if (
        quote.businessModeCode !== 'trade' &&
        quote.businessModeCode !== 'service'
      )
        modes[quote.businessModeCode] = item.discount;
      return {
        ...repriceQuoteItem(item, mode),
        discount: quoteDiscountForMode(
          { ...item, modeDiscounts: modes },
          mode,
          quote.businessModeCode,
        ),
        modeDiscounts: modes,
      };
    }),
  };
}

export function planProductPrice(
  packageItemPrice: number | null,
  planDiscount: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.planProductPrice,
    { packageItemPrice, planDiscount },
    '方案价计算失败',
  );
}

/** Quote-workbench only calculation. The device's manually maintained unit
 * price remains the factor; this result is never written back to equipment. */
export function unitPriceSubtotal(unitPrice: number, quantity: number): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.unitPriceSubtotal,
    { deviceUnitPrice: unitPrice, quantity },
    '小计单价/月计算失败',
  );
}

function discountedQuotePricingBase(
  pricingBase: number,
  planDiscount: number,
): number {
  assertNonNegativeMoney(pricingBase, '计价基础');
  assertDiscountPercent(planDiscount, '方案专属折扣');
  return planProductPrice(pricingBase, planDiscount);
}

/** A relation-scoped read-only result. It never becomes a PackageProductLink fact. */
export function planProductSubtotal(
  packageItemPrice: number | null,
  planDiscount: number,
  relationQuantity: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.planProductSubtotal,
    { packageItemPrice, planDiscount, quantity: relationQuantity },
    '方案价小计计算失败',
  );
}

/**
 * One scoped value identity for the plan subtotal.  The relationship row is
 * the base scope; package and plan rows aggregate the same values instead of
 * introducing a second "plan package price" field.
 */
export function planSubtotal(
  relations: readonly Pick<
    PackageProductLink,
    'packageItemPrice' | 'quantity'
  >[],
  planDiscount: number,
): number {
  return safeMoneyTotal(
    relations.map((relation) =>
      planProductSubtotal(
        relation.packageItemPrice,
        planDiscount,
        relation.quantity,
      ),
    ),
    '方案合计',
  );
}

export function packageMonthlySubtotal(
  relations: readonly Pick<
    PackageProductLink,
    'packageItemPrice' | 'quantity'
  >[],
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.packageOriginalPrice,
    { packageRelations: relations },
    '套餐价小计/月计算失败',
  );
}

/** @deprecated Historical snapshot compatibility only. New code uses
 * packageMonthlySubtotal: it is the same relation-subtotal aggregation. */
export const packageOriginalPrice = packageMonthlySubtotal;

/**
 * Read-only monthly subtotal for one package-to-device relationship.
 * The package total must be derived from these same relation facts.
 */
export function packageItemMonthlySubtotal(
  relation: Pick<PackageProductLink, 'packageItemPrice' | 'quantity'>,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal,
    {
      packageItemPrice: relation.packageItemPrice,
      quantity: relation.quantity,
    },
    '套餐单品价小计计算失败',
  );
}

export function planPackagePrice(
  relations: readonly Pick<
    PackageProductLink,
    'packageItemPrice' | 'quantity'
  >[],
  planDiscount: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.planPackagePrice,
    { packageRelations: relations, planDiscount },
    '方案套餐价格计算失败',
  );
}

export function resolvedQuotePricingBase(item: PricedLine): number {
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment))
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
  if (item.pricingBaseSnapshot?.kind === 'buyout-tax13') {
    assertNonNegativeMoney(item.pricingBaseSnapshot.amount, '买断计价基础');
    return item.pricingBaseSnapshot.amount;
  }
  const localLineCents = quoteLocalExclusiveLineCents(item);
  if (localLineCents !== undefined) return localLineCents / 100 / item.quantity;
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment)) {
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
    const cents = item.fragmentAllocation?.allocatedRentalLineCents;
    if (!Number.isSafeInteger(cents) || (cents ?? -1) < 0)
      throw new Error('分行缺少准确来源金额');
    return (cents as number) / 100 / item.quantity;
  }
  const snapshot = item.pricingBaseSnapshot;
  if (snapshot?.calculationVersion !== 'pricing-v2') {
    throw new Error('报价行缺少明确计价基础快照');
  }
  assertNonNegativeMoney(snapshot.amount, '计价基础');
  if (item.packageItemPriceSnapshot !== undefined) {
    assertNonNegativeMoney(item.packageItemPriceSnapshot, '套餐单品价');
    if (
      snapshot.kind !== 'package-item' ||
      snapshot.amount !== item.packageItemPriceSnapshot
    ) {
      throw new Error('套餐计价基础与关系快照不一致');
    }
  } else if (snapshot.kind === 'package-item') {
    throw new Error('套餐计价基础缺少关系快照');
  }
  if (hasQuotePlanPricing(item)) {
    if (item.planProductPriceSnapshot === undefined)
      return discountedQuotePricingBase(
        snapshot.amount,
        item.planDiscountSnapshot ?? 100,
      );
    assertNonNegativeMoney(item.planProductPriceSnapshot, '方案分摊价');
    return item.planProductPriceSnapshot;
  }
  return snapshot.amount;
}

/** Compatibility name for existing consumers; the value now comes solely
 * from the typed pricing snapshot, never QuoteItem.unitPrice. */
export function resolvedQuoteUnitPrice(item: PricedLine): number {
  return resolvedQuotePricingBase(item);
}

export function occurrencePlanUnitPriceCents(
  baseCents: number,
  coefficient: string,
): number {
  if (!Number.isSafeInteger(baseCents) || baseCents < 0)
    throw new Error('方案实例缺少套餐单品价');
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,18})?$/.test(coefficient))
    throw new Error('方案专属比例无效');
  const [whole, fraction = ''] = coefficient.split('.');
  const scale = 10n ** BigInt(fraction.length);
  const numerator = BigInt(whole) * scale + BigInt(fraction || '0');
  // 方案专属单价先按分 HALF_UP，再乘套餐关系数量；计价视图和采购价
  // 只保留来源审计，不参与这两个报价字段。
  const unitCents = (BigInt(baseCents) * numerator * 2n + scale) / (scale * 2n);
  if (unitCents > BigInt(Number.MAX_SAFE_INTEGER))
    throw new Error('方案整行金额超出安全范围');
  return Number(unitCents);
}

export function occurrenceSourceLineCents(
  item: Pick<
    QuoteItem,
    'formalPlanOccurrence' | 'packageItemPriceSnapshot' | 'quantity'
  >,
): number {
  const tuple = item.formalPlanOccurrence;
  if (!tuple) throw new Error('缺少方案实例计价来源');
  if (!Number.isSafeInteger(item.quantity) || item.quantity < 1)
    throw new Error('方案实例数量无效');
  const baseCents = Math.round((item.packageItemPriceSnapshot ?? NaN) * 100);
  const amount =
    BigInt(occurrencePlanUnitPriceCents(baseCents, tuple.pricingCoefficient)) *
    BigInt(item.quantity);
  if (amount > BigInt(Number.MAX_SAFE_INTEGER))
    throw new Error('方案整行金额超出安全范围');
  return Number(amount);
}

/** 空白/默认与免押租赁共享租赁实例计价；仅设备购买切换购买价。 */
export function occurrencePricingView(
  businessModeCode: BusinessModeCode,
): 'rental' | 'purchase' {
  return businessModeCode === 'buyout' ? 'purchase' : 'rental';
}

export function lineTotal(item: PricedLine): number {
  const effectiveDiscount = quoteItemEffectiveDiscount(item, item.discount);
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment))
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
  if (item.pricingBaseSnapshot?.kind === 'buyout-tax13') {
    return finalDiscountedRelationLine(
      item.pricingBaseSnapshot.amount,
      effectiveDiscount,
      item.quantity,
    );
  }
  const localLineCents = quoteLocalExclusiveLineCents(item);
  if (localLineCents !== undefined) {
    const policyBaseCents =
      quoteDiscountParticipation(item) === 'excluded' &&
      hasQuotePlanPricing(item) &&
      item.packageItemPriceSnapshot !== undefined
        ? Math.max(
            Math.round(item.packageItemPriceSnapshot * item.quantity * 100),
            localLineCents,
          )
        : localLineCents;
    return discountedLineCents(policyBaseCents, effectiveDiscount) / 100;
  }
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment)) {
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
    const cents = item.fragmentAllocation?.allocatedRentalLineCents;
    if (!Number.isSafeInteger(cents) || (cents ?? -1) < 0)
      throw new Error('分行缺少准确来源金额');
    return discountedLineCents(cents as number, effectiveDiscount) / 100;
  }
  if (item.formalPlanOccurrence) {
    // planProductPriceSnapshot 非空标识现代公式；旧冻结版本继续读取原始
    // sourceLineCents，不用新规则篡改已经发布的历史金额。
    const cents =
      item.planProductPriceSnapshot === undefined ||
      item.packageItemPriceSnapshot === undefined
        ? item.formalPlanOccurrence.sourceLineCents
        : occurrenceSourceLineCents(item);
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error('方案实例历史行金额无效');
    return discountedLineCents(cents, effectiveDiscount) / 100;
  }
  if (
    hasQuotePlanPricing(item) &&
    item.planProductPriceSnapshot !== undefined &&
    item.planProductSubtotalSnapshot !== undefined
  ) {
    // An allocated flat-plan line can differ by one cent from its rounded
    // display unit times quantity. The frozen whole-line allocation wins.
    const cents =
      quoteDiscountParticipation(item) === 'excluded' &&
      item.packageItemPriceSnapshot !== undefined
        ? Math.round(item.packageItemPriceSnapshot * item.quantity * 100)
        : Math.round(item.planProductSubtotalSnapshot * 100);
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error('方案分摊行金额无效');
    return discountedLineCents(cents, effectiveDiscount) / 100;
  }
  if (
    item.sourceType === 'package' &&
    item.packageItemMonthlySubtotalSnapshot !== undefined &&
    item.packageRelationQuantitySnapshot !== undefined &&
    Number.isSafeInteger(item.packageRelationQuantitySnapshot) &&
    item.packageRelationQuantitySnapshot > 0 &&
    Number.isSafeInteger(item.quantity) &&
    item.quantity % item.packageRelationQuantitySnapshot === 0
  ) {
    const cents =
      Math.round(item.packageItemMonthlySubtotalSnapshot * 100) *
      (item.quantity / item.packageRelationQuantitySnapshot);
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error('套餐成员冻结行金额无效');
    return discountedLineCents(cents, effectiveDiscount) / 100;
  }
  const pricingBase = item.pricingBaseSnapshot;
  if (pricingBase?.calculationVersion !== 'pricing-v2')
    throw new Error('报价行缺少明确计价基础快照');
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine,
    {
      currentPricingBase:
        hasQuotePlanPricing(item) && item.planProductPriceSnapshot !== undefined
          ? item.planProductPriceSnapshot
          : pricingBase.amount,
      planDiscount:
        hasQuotePlanPricing(item) && item.planProductPriceSnapshot === undefined
          ? (item.planDiscountSnapshot ?? 100)
          : 100,
      itemDiscount: effectiveDiscount,
      quantity: item.quantity,
    },
    '产品最终折后价计算失败',
  );
}

/** Monthly reference fields keep rental pricing even on a purchase quote. */
export function rentalLineTotal(
  item: QuoteItem,
  businessModeCode?: BusinessModeCode,
): number {
  const currentMode =
    businessModeCode ??
    (item.pricingBaseSnapshot?.kind === 'buyout-tax13' ? 'buyout' : 'rental');
  const rentalMode =
    isRentalBusinessMode(currentMode) || currentMode === 'full'
      ? currentMode
      : 'rental';
  if (item.pricingBaseSnapshot?.kind === 'buyout-tax13') {
    const rentalBase =
      item.packageItemPriceSnapshot !== undefined
        ? item.packageItemPriceSnapshot
        : item.deviceFieldSnapshot?.rentalPrice;
    if (
      typeof rentalBase !== 'number' ||
      !Number.isFinite(rentalBase) ||
      rentalBase < 0
    )
      return Number.NaN;
  }
  const rentalItem =
    item.pricingBaseSnapshot?.kind === 'buyout-tax13'
      ? repriceQuoteItem(item, rentalMode)
      : item;
  return lineTotal({
    ...rentalItem,
    discount: quoteDiscountForMode(item, rentalMode, currentMode),
  });
}

/** 已读回版本使用服务端冻结租赁行事实；当前草稿编辑才按同一行规则派生。 */
export function quoteFinalRentalSubtotalCents(
  quote: Pick<Quote, 'items' | 'dirty' | 'businessModeCode'>,
): number {
  const lineCents = quote.items.map((item) => {
    const cents =
      !quote.dirty && item.formalRentalLineTotalCents !== undefined
        ? item.formalRentalLineTotalCents
        : Math.round(rentalLineTotal(item, quote.businessModeCode) * 100);
    if (Number.isNaN(cents)) return Number.NaN;
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error('租赁成交额超出安全分范围');
    return cents;
  });
  if (lineCents.some(Number.isNaN)) return Number.NaN;
  return safeCentsTotal(lineCents, '租赁成交额');
}

export function quoteSubtotal(items: PricedLine[]): number {
  return safeMoneyTotal(items.map(lineTotal), '报价合计');
}

/** “整单折扣”是批量编辑动作：覆盖当前所有行，但允许用户随后单独微调任意行。 */
export function applyOverallDiscount<
  T extends { items: QuoteItem[]; overallDiscount: number },
>(quote: T, overallDiscount: number): T {
  assertDiscountPercent(overallDiscount, '整单折扣');
  return {
    ...quote,
    overallDiscount,
    items: quote.items.map((item) =>
      quoteDiscountParticipation(item) === 'excluded'
        ? item
        : { ...item, discount: overallDiscount },
    ),
  };
}

export function quoteTotal(quote: {
  items: PricedLine[];
  overallDiscount: number;
}): number {
  assertDiscountPercent(quote.overallDiscount, '整单折扣');
  // 整单折扣是批量写入每个报价行的操作基线，不是第二层价格因子。
  return quoteSubtotal(quote.items);
}

export function portfolioQuoteTotal(
  quotes: readonly { items: PricedLine[]; overallDiscount: number }[],
): number {
  return safeMoneyTotal(quotes.map(quoteTotal), '报价组合合计');
}

export function quoteItemQuantity(
  items: readonly Pick<QuoteItem, 'quantity'>[],
): number {
  return items.reduce((sum, item) => {
    assertQuoteQuantity(item.quantity);
    return sum + item.quantity;
  }, 0);
}

export function quoteAmounts(quote: {
  items: PricedLine[];
  overallDiscount: number;
}): { subtotal: number; overallDiscount: number; total: number } {
  assertDiscountPercent(quote.overallDiscount, '整单折扣');
  const subtotal = quoteSubtotal(quote.items);
  return {
    subtotal,
    overallDiscount: quote.overallDiscount,
    total: subtotal,
  };
}

export function quoteMonthlySubtotal(
  rentalPrice: number,
  quantity: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.rentalSubtotal,
    { rentalPrice, quantity },
    '小计租金计算失败',
  );
}

export function quoteMonthlyItemsSubtotal(
  items: readonly Pick<QuoteItem, 'pricingBaseSnapshot' | 'quantity'>[],
): number {
  return safeMoneyTotal(
    items.map((item) => {
      assertQuoteQuantity(item.quantity);
      if (item.pricingBaseSnapshot?.kind !== 'rental') {
        throw new Error('月租金小计只能消费租金计价快照');
      }
      return quoteMonthlySubtotal(
        item.pricingBaseSnapshot.amount,
        item.quantity,
      );
    }),
    '月租金合计',
  );
}

export function finalDiscountedRelationLine(
  planAdjustedRelationPrice: number,
  itemDiscount: number,
  quantity: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine,
    {
      currentPricingBase: planAdjustedRelationPrice,
      planDiscount: 100,
      itemDiscount,
      quantity,
    },
    '产品最终折后价计算失败',
  );
}

export function packageDiscountedUnitPrice(
  packageItemPrice: number,
  planDiscount: number,
  itemDiscount: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice,
    { packageItemPrice, planDiscount, itemDiscount },
    '套餐最终折后价计算失败',
  );
}

export function packageDiscountedSubtotal(
  packageItemPrice: number,
  planDiscount: number,
  itemDiscount: number,
  quantity: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal,
    { packageItemPrice, planDiscount, itemDiscount, quantity },
    '套餐最终折后价小计计算失败',
  );
}

/** Purchase output always uses the frozen buyout price, regardless of quote mode. */
export function purchaseFinalPriceTax13(
  item: {
    deviceFieldSnapshot?: Pick<
      NonNullable<QuoteItem['deviceFieldSnapshot']>,
      'rentalPrice' | 'taxInclusivePlanPrice'
    >;
    discount: number;
    modeDiscounts?: QuoteItem['modeDiscounts'];
    quantity: number;
  },
  businessModeCode: BusinessModeCode,
): number {
  const source = item.deviceFieldSnapshot?.taxInclusivePlanPrice;
  if (source == null || !Number.isFinite(source) || source < 0)
    return Number.NaN;
  return packageDiscountedSubtotal(
    source,
    100,
    quoteDiscountForMode(item, 'buyout', businessModeCode),
    item.quantity,
  );
}

/** The approved ex-tax result is exposed only for default/rental views and
 * always uses the frozen untaxed plan price. */
export function purchaseFinalPriceExTax13(
  item: {
    deviceFieldSnapshot?: Pick<
      NonNullable<QuoteItem['deviceFieldSnapshot']>,
      'untaxedPlanPrice'
    >;
    discount: number;
    modeDiscounts?: QuoteItem['modeDiscounts'];
    quantity: number;
  },
  businessModeCode: BusinessModeCode = 'full',
): number {
  const source = item.deviceFieldSnapshot?.untaxedPlanPrice;
  if (source == null || !Number.isFinite(source) || source < 0)
    return Number.NaN;
  return packageDiscountedSubtotal(
    source,
    100,
    quoteDiscountForMode(item, 'buyout', businessModeCode),
    item.quantity,
  );
}

export function dynamicCostSubtotal(
  dynamicCost: number,
  quantity: number,
): number {
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.dynamicCostSubtotal,
    { dynamicCost, quantity },
    '动态成本小计计算失败',
  );
}

export function packageDiscountedQuote(items: readonly PricedLine[]): number {
  if (items.some((item) => item.localPlanInstanceId))
    return safeMoneyTotal(items.map(lineTotal), '套餐折后合计');
  if (items.some((item) => item.sourceFragment))
    return safeMoneyTotal(items.map(lineTotal), '套餐折后合计');
  if (items.some((item) => quoteLocalExclusiveLineCents(item) !== undefined))
    return safeMoneyTotal(items.map(lineTotal), '套餐折后合计');
  return evaluateApprovedSystemCalculation(
    SYSTEM_CALCULATION_REFS.packageDiscountedQuote,
    {
      pricedLines: items.map((item) => ({
        pricingBaseAmount: item.pricingBaseSnapshot?.amount ?? NaN,
        planDiscount:
          item.pricingBaseSnapshot?.kind === 'buyout-tax13'
            ? 100
            : hasQuotePlanPricing(item)
              ? (item.planDiscountSnapshot ?? 100)
              : 100,
        itemDiscount: item.discount,
        quantity: item.quantity,
      })),
    },
    '套餐最终折后价计算失败',
  );
}
